import { excludeArrayFromArray } from "./Functions";

// Derived in the browser from two fields the order report already returns: the
// part of the grand total the franchise did not collect in cash is what the
// customer paid online. It is never sent to the backend in ex_list — no
// serializer field of this name exists and the endpoint answers 200 with an
// error envelope for unknown names.
export const ONLINE_PAYMENT_AMOUNT = "online_payment_amount";

// franchise_charge holds the weather surge (Extra_charge), so the reports call it that (Ajmal, 2026-10-24).
const COLUMN_LABELS = { franchise_charge: "surge" };

export const columnLabel = (column) => COLUMN_LABELS[column] || column.replaceAll("_", " ");

// The key this column was saved under before it was named for what it holds;
// browsers keep the column order in localStorage, so old lists still carry it.
const LEGACY_ONLINE_PAYMENT_AMOUNT = "grand_total_minus_franchise_collected";

export const migrateLegacyColumns = (columns) => {
  if (!Array.isArray(columns)) return columns;
  return columns.map((column) =>
    column === LEGACY_ONLINE_PAYMENT_AMOUNT ? ONLINE_PAYMENT_AMOUNT : column
  );
};

const toAmount = (value) => {
  if (value === null || value === undefined || value === "") return NaN;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : NaN;
};

export const onlinePaymentAmount = (row) => {
  const grandTotal = toAmount(row && row.grand_total);
  const collected = toAmount(row && row.franchise_collected_amount);
  if (Number.isNaN(grandTotal) || Number.isNaN(collected)) return "--";
  return (grandTotal - collected).toFixed(2);
};

export const withDerivedColumns = (rows) => {
  if (!Array.isArray(rows)) return rows;
  return rows.map((row) => ({
    ...row,
    [ONLINE_PAYMENT_AMOUNT]: onlinePaymentAmount(row),
  }));
};

const toAmountOrZero = (value) => {
  const amount = toAmount(value);
  return Number.isNaN(amount) ? 0 : amount;
};

export const DELIVERY_CHARGE_WAIVED = "delivery_charge_waived";

export const deliveryChargeDisplay = (row) => {
  const paid = toAmountOrZero(row && row.delivery_charge);
  const waived = toAmountOrZero(row && row[DELIVERY_CHARGE_WAIVED]);
  return { struck: waived > 0 ? (paid + waived).toFixed(2) : null, shown: paid.toFixed(2) };
};

// Real serializer fields the report never offers or shows. They stay here, not in
// the page's column list, so they are always sent in ex_list and stripped from
// any column order a browser saved before they were hidden.
export const HIDDEN_COLUMNS = [
  "in_profit",
  "commission_on_franchise",
  "commission_amount_on_franchise",
  "franchise_net_balance",
];

export const withoutHiddenColumns = (columns) => {
  if (!Array.isArray(columns)) return columns;
  return columns.filter((column) => !HIDDEN_COLUMNS.includes(column));
};

// The report sends the columns it does NOT want as ex_list, and the backend pops
// each name off its serializer. Two names never go in it: the derived column,
// which no serializer field matches, and the waived charge, which the delivery
// cell reads to strike out its own value even when the waived column is hidden.
export const reportExList = (selectedColumns) => {
  const chosen = excludeArrayFromArray([], selectedColumns || []).filter(
    (column) => column !== ONLINE_PAYMENT_AMOUNT && column !== DELIVERY_CHARGE_WAIVED
  );
  return [...withoutHiddenColumns(chosen), ...HIDDEN_COLUMNS];
};

// Percentages the backend copies onto each report row (accounts/logics.py:
// branch.commission, franchise.commission, order.commission_rate, the GST slab).
// extra_commission_rate is rupees despite its name and is deliberately absent.
export const PERCENTAGE_COLUMNS = [
  "extra_commission_perc",
  "branch_gst_perc",
  "commission_on_restaurent",
  "commission_on_franchise",
];

export const isPercentageColumn = (column) => PERCENTAGE_COLUMNS.includes(column);

const UNTOTALLED_COLUMNS = ["order", "user_of_intrest", "delivery_boy"];

export const footerCell = (rows, column) => {
  if (column === "id") return "Total";
  if (UNTOTALLED_COLUMNS.includes(column)) return "--";
  if (!Array.isArray(rows) || rows.length === 0) return "--";

  const sum = rows.reduce((total, row) => total + toAmountOrZero(row && row[column]), 0);
  if (isPercentageColumn(column)) return `${(sum / rows.length).toFixed(2)} %`;

  const hasNumber = rows.some((row) => !Number.isNaN(toAmount(row && row[column])));
  return hasNumber ? `₹ ${sum.toFixed(2)}` : "--";
};
